/* ----------------------------------------------------------------------------
 * Approval preview/approve model (Option a, owner 2026-07-19). STANDALONE,
 * DOM-free, import-free helpers + shared types, per this repo's FE test
 * convention (`node --test --experimental-strip-types`). ApprovalActionPanel is
 * a thin view over these helpers (mirrors ContentActionPanel over
 * contentActionModel); the data client (src/data/approvals.ts) imports its TYPES
 * from here. Keeping this file dependency-free is deliberate: it lets the test
 * exercise the real approve/idempotency logic without pulling in the browser-only
 * api client.
 *
 * Purpose: let a human SEE the exact CRM record the platform proposes to create
 * — every field of proposed_action.payload, rendered faithfully and un-summarised
 * — plus the advisory recommendation, BEFORE they click Approve. The human click
 * is the ONLY path to a decision: no auto-approve, no bulk-approve, and the
 * runner fires the underlying decide AT MOST once per mounted decision.
 * -------------------------------------------------------------------------- */

/* ---- Shared types (the ApprovalsController::publicShape contract) ---------- */

/** method+path the platform preparer proposed, plus the concrete payload the
 *  human must see BEFORE approving. */
export interface NativeProposedAction {
  method: string;
  path: string;
  payload?: Record<string, unknown> | null;
}

/** The advisory recommendation attached to a proposed approval. */
export interface ApprovalRecommendation {
  subject?: string;
  body?: string;
  grounded?: boolean;
  consent?: boolean;
  ai?: unknown;
  [k: string]: unknown;
}

/** Public projection returned by ApprovalsController::publicShape. */
export interface PublicApproval {
  id: number;
  action_class: string;
  source: string;
  status: 'proposed' | 'approved' | 'rejected' | 'cancelled' | 'executed' | 'failed' | string;
  recommendation: ApprovalRecommendation | null;
  proposed_action: NativeProposedAction | null;
  evidence_refs: unknown;
  verifier_result: { pass?: boolean; notes?: string[] } | null;
  decided_by: string | null;
  decided_at: string | null;
  executed_at: string | null;
  result_ref: string | null;
  attempt_count: number | null;
  error_text: string | null;
  created_at: string | null;
}

export type DecisionKind = 'approve' | 'reject';

export interface DecisionResult {
  outcome?: string;
  [k: string]: unknown;
}

/* ---- Control allowlist ----------------------------------------------------- */

/** Controls this panel is ALLOWED to expose. Any other verb is forbidden by
 *  construction — the UI must never be able to create or bulk-apply. */
export const ALLOWED_DECISION_CONTROLS = ['approve', 'reject', 'cancel'] as const;
export const FORBIDDEN_DECISION_CONTROLS = ['create', 'approve-all', 'bulk-approve', 'auto-approve'] as const;
export type AllowedDecisionControl = (typeof ALLOWED_DECISION_CONTROLS)[number];

export function isAllowedDecisionControl(verb: string): verb is AllowedDecisionControl {
  return (ALLOWED_DECISION_CONTROLS as readonly string[]).includes(verb);
}

/* ---- Pure view helpers ----------------------------------------------------- */

/** Only `proposed` items are pending an owner decision. */
export function isPending(a: Pick<PublicApproval, 'status'>): boolean {
  return a.status === 'proposed';
}

/** Recommendation subject/body as plain strings (never markdown; server-clean). */
export function recommendationText(a: Pick<PublicApproval, 'recommendation'>): { subject: string; body: string } {
  const rec = a.recommendation ?? null;
  return {
    subject: typeof rec?.subject === 'string' ? rec.subject : '',
    body: typeof rec?.body === 'string' ? rec.body : '',
  };
}

/** Render a payload value faithfully as a display string. Objects/arrays are
 *  JSON so nested structure is still shown, never dropped. */
export function displayPayloadValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export interface PayloadRow {
  key: string;
  value: string;
}

/** EVERY field of proposed_action.payload, in payload order — the exact record a
 *  human must review before approving. Nothing is hidden, filtered, or summarised.
 *  Returns [] when there is no payload (e.g. an advisory-only item). */
export function payloadPreviewRows(action: NativeProposedAction | null | undefined): PayloadRow[] {
  const payload = action?.payload;
  if (!payload || typeof payload !== 'object') return [];
  return Object.keys(payload).map((key) => ({
    key,
    value: displayPayloadValue((payload as Record<string, unknown>)[key]),
  }));
}

/** Human-readable "what will be created" line (method + path). */
export function proposedActionSummary(action: NativeProposedAction | null | undefined): string {
  if (!action) return 'No concrete action is attached to this item.';
  return `${(action.method || '').toUpperCase()} ${action.path}`.trim();
}

/** The explicit affirmation shown on the Approve confirm step: names exactly what
 *  approving will create, and how many fields the human is confirming. */
export function confirmAffirmation(a: Pick<PublicApproval, 'proposed_action'>): string {
  const rows = payloadPreviewRows(a.proposed_action);
  const target = proposedActionSummary(a.proposed_action);
  if (rows.length === 0) {
    return `Approve will run: ${target}. Nothing else will happen.`;
  }
  const noun = rows.length === 1 ? 'field' : 'fields';
  return `Approve will create exactly this record via ${target}, with the ${rows.length} ${noun} shown above. Nothing else will happen.`;
}

/* ---- Decision idempotency + one-shot runner -------------------------------- */

/** A STABLE idempotency key for a decision on a specific approval. The SAME
 *  (id, decision) always yields the SAME key, so a retry de-dupes at the server
 *  replay cache and can never double-apply. */
export function decisionIdempotencyKey(id: number, decision: DecisionKind | 'cancel'): string {
  return `approval-${id}-${decision}`;
}

/** Injected decision dependencies (real client in the panel; a mock in tests). */
export interface DecisionDeps {
  decide(id: number, decision: DecisionKind, idempotencyKey: string, note?: string): Promise<DecisionResult>;
  cancel(id: number, idempotencyKey: string): Promise<DecisionResult>;
}

export type DecisionOutcome =
  | { status: 'done'; decision: DecisionKind | 'cancel'; idempotencyKey: string; result: DecisionResult }
  | { status: 'ignored' }; // a repeat call after the first — the click already fired once

/** One-shot decision runner. Guarantees the underlying decide/cancel fires AT
 *  MOST ONCE for this mounted decision even if invoked repeatedly (double-click,
 *  re-render, event replay), and always carries the STABLE per-(id,decision)
 *  idempotency key. This is the human-click-is-the-only-path guard, made testable
 *  without a DOM. There is intentionally NO batch/all method. */
export function createDecisionRunner(deps: DecisionDeps) {
  let locked = false;
  let lastKey: string | null = null;

  async function run(
    a: Pick<PublicApproval, 'id'>,
    decision: DecisionKind | 'cancel',
    note?: string,
  ): Promise<DecisionOutcome> {
    if (locked) return { status: 'ignored' };
    locked = true;
    const key = decisionIdempotencyKey(a.id, decision);
    lastKey = key;
    const result =
      decision === 'cancel'
        ? await deps.cancel(a.id, key)
        : await deps.decide(a.id, decision, key, note);
    return { status: 'done', decision, idempotencyKey: key, result };
  }

  return {
    get locked() {
      return locked;
    },
    get lastKey() {
      return lastKey;
    },
    approve: (a: Pick<PublicApproval, 'id'>, note?: string) => run(a, 'approve', note),
    reject: (a: Pick<PublicApproval, 'id'>, note?: string) => run(a, 'reject', note),
    cancel: (a: Pick<PublicApproval, 'id'>) => run(a, 'cancel'),
  };
}
